/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 체크박스그룹 클래스를 정의한다.
 *
 * @file /src/components/form/field/check/Group.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 5. 15.
 */
import * as parent from '@components/form/field/Field';
import { Check } from '@elements/check/Check';
import { LanguageText } from '@utils/Language';
import { Renderer } from '@utils/Renderer';
import './Group.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 한줄당 표시될 선택항목 갯수
     */
    columns?: number;

    /**
     * 선택항목
     */
    options: { [onValue: string]: LanguageText | null };

    /**
     * 선택항목 출력방식
     */
    displayType?: 'input' | 'box';

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Group extends parent.Field {
    /**
     * 컴포넌트 확장타입
     */
    variant = 'checkgroup';

    /**
     * 한줄당 표시될 선택항목 갯수
     */
    columns: number;

    /**
     * 표시방법
     */
    displayType: 'input' | 'box';

    /**
     * 선택항목
     */
    options: { [onValue: string]: LanguageText | null } = {};

    /**
     * 체크박스 엘리먼트
     */
    checks: Check[];

    /**
     * 체크박스그룹 필드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Group> = null) {
        super(properties);

        this.displayType = this.properties?.displayType == 'box' ? 'box' : 'input';
        this.columns = this.properties.columns ?? 1;
        this.options = this.properties.options ?? {};
        this.initChecks();
    }

    /**
     * 체크박스 엘리먼트를 정의한다.
     */
    initChecks(): void {
        this.checks = [];
        for (const [value, label] of Object.entries(this.options)) {
            this.checks.push(
                new Check({
                    type: 'check',
                    label: label,
                    value: value,
                    displayType: this.displayType,
                    listeners: {
                        change: async (me) => {
                            const value = me.getValue();
                            if (typeof value == 'string') {
                                await this.updateValue();
                            }
                        },
                    },
                })
            );
        }
    }

    /**
     * 옵션값을 추가한다.
     *
     * @param value - 값
     * @param display - 표시될 값
     */
    addOption(value: string, display: LanguageText | null): void {
        if (this.options[value] === undefined) {
            this.options[value] = display;
            this.checks.push(
                new Check({
                    type: 'check',
                    label: display,
                    value: value,
                })
            );
        }

        // @todo 렌더링 하기
    }

    /**
     * 체크 상태변경에 따라 필드값을 업데이트한다.
     */
    async updateValue(): Promise<void> {
        const values: string[] = [];
        for (const check of this.checks) {
            if (check.isChecked() == true) {
                const value = check.getValue();
                if (typeof value == 'string') {
                    values.push(value);
                }
            }
        }

        await this.setValue(values);
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     */
    async setValue(value: string[], is_init: boolean = false): Promise<void> {
        await this.lockValue();

        const values: string[] = [];
        for (const check of this.checks) {
            if (value.indexOf(check.value) > -1) {
                check.setValue(true, false);
                values.push(check.value);
            } else {
                check.setValue(false, false);
            }
        }

        await this.releaseValue(values, is_init);
    }

    /**
     * 필드값이 비어있는지 확인한다.
     *
     * @returns 빈값여부
     */
    async isBlank(): Promise<boolean> {
        const values = await this.getValue();
        return values == null || values.length == 0;
    }

    /**
     * 컴포넌트의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean): this {
        for (const check of this.checks) {
            check.setDisabled(disabled);
        }

        return super.setDisabled(disabled);
    }

    /**
     * 컴포넌트 DOM 을 렌더링한다.
     */
    async renderDom(): Promise<void> {
        await super.renderDom();
        this.$getComponent().setStyleProperty('--aui-field-check-columns', this.columns);
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        for (const check of this.checks) {
            this.$getContent().append(check.$getElement());
        }

        await Promise.all((this.checks ?? []).map((check) => Renderer.request(() => check.render())));
    }
}
